import { FC, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useStore } from '@hooks/useStore';
import { useMyTranslations } from '@hooks/useMyTranslations';
import { BrandTabsTransFields, BrandTabType, IBrandTabsProps } from './types';
import { brandTabs, tabsToUrl } from './constants';
import { brandTabsTranslations } from './translations';

const BrandTabs: FC<IBrandTabsProps> = observer(({ currentTab, disabled = false }) => {
  const {
    brandsStore: { brand },
    pointsStore: { pointsList },
    productsStore: { productsList },
    cataloguesStore: { cataloguesList },
  } = useStore();

  const { t } = useMyTranslations<BrandTabsTransFields>([brandTabsTranslations.name]);

  const { pathname } = useLocation();
  const navigate = useNavigate();

  const onChangeTab = useCallback((tab: BrandTabType) => {
    if (tab !== currentTab) {
      const url = pathname.split('/').slice(0, 3).join('/');
      navigate(url + tabsToUrl[tab]);
    }
  }, []);

  const isDisabled = useCallback(
    (tab: BrandTabType): boolean => {
      if (disabled) return true;
      switch (tab) {
        case 'point list':
        case 'product list':
          // TODO uncommented after feature will be unfrosted
          // case 'catalogue list':
          return brand?.status === 'Revise';
        default:
          return false;
      }
    },
    [brand?.status, disabled]
  );

  const renderCounter = useCallback(
    (tab: BrandTabType) => {
      const counterStyle = 'rounded-[12px] p-[4px] bg-primary text-white small-text min-w-[24px]';
      switch (tab) {
        case 'point list':
          return (
            <div className={counterStyle}>{pointsList?.length || brand?.pointsCount || 0}</div>
          );
        case 'product list':
          return (
            <div className={counterStyle}>{productsList?.length || brand?.productsCount || 0}</div>
          );
        // TODO uncommented after feature will be unfrosted
        // case 'catalogue list':
        //   return (
        //     <div className={counterStyle}>
        //       {cataloguesList?.length || brand?.cataloguesCount || 0}
        //     </div>
        //   );
        default:
          return null;
      }
    },
    [brand, pointsList, productsList, cataloguesList]
  );

  return (
    <div className="flex items-center gap-x-[24px] overflow-x-scroll px-[48px] sm-page-padding scrollbar-hide min-h-[34px]">
      {brandTabs.map((item) => (
        <button
          key={item}
          className={`relative h-[34px] bg-transparent flex items-center gap-x-[4px] cursor-pointer pb-[8px] ${
            currentTab === item && 'border-b-[2px] border-primary '
          } ${isDisabled(item) && 'disabled:cursor-not-allowed'}`}
          type="button"
          onClick={() => onChangeTab(item)}
          disabled={isDisabled(item)}
        >
          <span
            className={`text-[16px] leading-[16px] text-[#A3AAB3] text-nowrap uppercase ${
              currentTab === item && '!text-basic-text'
            }`}
          >
            {t(item)}
          </span>
          {renderCounter(item)}
        </button>
      ))}
    </div>
  );
});

export default BrandTabs;
